import React from 'react';
import { format, formatDistanceToNow } from 'date-fns/esm';
import {
  Bell,
  DollarSign,
  MessagesSquare,
  UserPlus,
  X
} from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';

const NotificationItem = ({ notification }) => {
  const { markAsRead, deleteNotification } = useNotifications();
  const navigate = useNavigate();
  
  const getIcon = () => {
    switch (notification.type) {
      case 'campaign_status':
        return <Bell className="h-6 w-6 text-blue-500" />;
      case 'payment':
        return <DollarSign className="h-6 w-6 text-green-500" />;
      case 'message':
        return <MessagesSquare className="h-6 w-6 text-purple-500" />;
      case 'referral':
        return <UserPlus className="h-6 w-6 text-orange-500" />;
      default:
        return <Bell className="h-6 w-6 text-gray-500" />;
    }
  };
  
  const handleClick = () => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Check if the URL contains '/brand/' to determine if we're on brand side
    const isBrandSide = window.location.pathname.includes('/brand/');
    
    // Handle navigation based on notification type and data
    if (notification.data) {
      switch (notification.type) {
        case 'campaign_status':
        case 'application_status':
          // Navigate to campaign details - use relatedId first, then campaignId
          if (notification.data.relatedId) {
            navigate(`/influencer/campaigns/${notification.data.relatedId}`);
          } else if (notification.data.campaignId) {
            navigate(`/influencer/campaigns/${notification.data.campaignId}`);
          } else {
            navigate('/influencer/campaigns');
          }
          break;
          
        case 'content_approval':
          // Navigate to campaign details where content submission status is shown
          if (notification.data.campaignId) {
            navigate(`/influencer/campaigns/${notification.data.campaignId}`);
          } else if (notification.data.relatedId) {
            navigate(`/influencer/campaigns/${notification.data.relatedId}`);
          } else {
            navigate('/influencer/campaigns');
          }
          break;
          
        case 'payment':
          // Navigate to payments page - can be specific payment or general page
          if (notification.data.relatedId && !isBrandSide) {
            navigate(`/influencer/payments?payment=${notification.data.relatedId}`);
          } else {
            navigate(isBrandSide ? '/brand/payments' : '/influencer/payments');
          }
          break;
          
        case 'message':
          // Navigate to messages, potentially to a specific conversation
          if (notification.data.relatedId) {
            navigate(isBrandSide 
              ? `/brand/messages?conversation=${notification.data.relatedId}` 
              : `/influencer/messages?conversation=${notification.data.relatedId}`);
          } else if (notification.data.conversationId) {
            navigate(isBrandSide 
              ? `/brand/messages?conversation=${notification.data.conversationId}` 
              : `/influencer/messages?conversation=${notification.data.conversationId}`);
          } else {
            navigate(isBrandSide ? '/brand/messages' : '/influencer/messages');
          }
          break;
          
        case 'referral':
          // Navigate to referrals page
          navigate('/influencer/referrals');
          break;
          
        // Brand-specific notification types
        case 'application':
          // Navigate to applications page or specific application
          if (notification.data.relatedId) {
            navigate(`/brand/applications?campaign=${notification.data.relatedId}`);
          } else if (notification.data.campaignId) {
            navigate(`/brand/applications?campaign=${notification.data.campaignId}`);
          } else {
            navigate('/brand/applications');
          }
          break;
          
        case 'content_submission':
          // Navigate to content approval detail page
          if (notification.data.relatedId) {
            navigate(`/brand/content-approval/${notification.data.relatedId}`);
          } else if (notification.data.campaignId) {
            navigate(`/brand/content-approval?campaign=${notification.data.campaignId}`);
          } else {
            navigate('/brand/content-approval');
          }
          break;
          
        case 'review_submission':
          // Navigate to review detail page
          if (notification.data.relatedId) {
            navigate(`/brand/content-approval/${notification.data.relatedId}`);
          } else if (notification.data.campaignId) {
            navigate(`/brand/content-approval?campaign=${notification.data.campaignId}`);
          } else {
            navigate('/brand/content-approval');
          }
          break;
          
        default:
          // Default to notifications page
          navigate(isBrandSide ? '/brand/notifications' : '/influencer/notifications');
          break;
      }
    } else {
      // If no specific data, go to notifications page
      navigate(isBrandSide ? '/brand/notifications' : '/influencer/notifications');
    }
  };
  
  const handleDelete = (e) => {
    e.stopPropagation();
    deleteNotification(notification.id);
  };
  
  const formattedTime = () => {
    try {
      const date = new Date(notification.timestamp);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return formatDistanceToNow(date, { addSuffix: true });
      } else {
        return format(date, 'MMM d, yyyy');
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };
  
  return (
    <div 
      onClick={handleClick}
      className={`flex items-start p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors duration-150 ${!notification.read ? 'bg-blue-50' : ''}`}
    >
      <div className="flex-shrink-0 mr-3">
        {getIcon()}
      </div>
      <div className="flex-grow min-w-0">
        <div className="flex justify-between items-start">
          <h4 className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
            {notification.title}
          </h4>
          <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
            {formattedTime()}
          </span>
        </div>
        <p className="text-sm text-gray-600 mt-1 truncate">
          {notification.message}
        </p>
      </div>
      <button 
        onClick={handleDelete} 
        className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
        aria-label="Delete notification"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
};

export default NotificationItem;
